/** Client fallback when /api/settings.php is unavailable (matches PHP defaults). */

import { mergePolicies, mergeAuthCopy, mergeStoreCopy, mergeBlogContent, mergePageCopy } from './content/siteCopyHelpers';

const CONTENT_DEFAULTS = {
  hero_title: 'Jewelry for every celebration',
  hero_subtitle: 'Handcrafted in India · shipped nationwide',
  hero_description:
    'Rakhi, Lumba, haldi & wedding sets — artisan pieces you will keep for years, not just a season.',
  hero_cta1_label: '',
  hero_cta1_url: '',
  hero_cta2_label: '',
  hero_cta2_url: '',
  home_about_title: 'Our Story',
  home_about_text:
    'Every piece at Saloni Designs is lovingly handcrafted with attention to detail that speaks of tradition and artistry. We believe in creating not just beautiful jewelry, but meaningful connections that celebrate the sacred bonds of family and love.',
  home_about_tagline: 'Crafted with love, rooted in tradition.',
  about_hero_title: 'About us',
  about_hero_subtitle: 'Where tradition meets contemporary elegance — every thread tells a story.',
  about_para1:
    'At Saloni Designs, we believe that every thread tells a story, every bead holds a memory, and every creation carries the essence of love and tradition. Founded with a passion for preserving the sacred art of Rakhi and Lumba making, we dedicate ourselves to crafting pieces that celebrate the beautiful bonds of family.',
  about_para2:
    'Each piece is thoughtfully designed and lovingly handcrafted, combining traditional techniques with contemporary aesthetics. We source fine materials and work with skilled artisans who share our commitment to quality and authenticity.',
  about_para3:
    "Whether you want a classic Rakhi that honors age-old traditions or a modern Lumba that speaks to today's style, our collections are meant to make every celebration meaningful.",
  about_tagline: 'Crafted with love, rooted in tradition, designed for today.',
  seo_home_title: 'Saloni Designs | Handcrafted Rakhi, Lumba & Festive Jewelry — Shop Online',
  seo_home_description:
    'Shop Saloni Designs — handcrafted Rakhi, Lumba, bracelets & festive jewelry. Authentic Indian designs shipped across India. Order online or visit our exhibitions.',
  seo_home_og_image: '',
  seo_shop_title: 'Shop Handcrafted Jewelry Collections | Saloni Designs',
  seo_shop_description:
    'Browse Rakhi, Lumba, bracelets, necklaces & more. Handcrafted Indian jewelry — shipped across India.',
  seo_shop_og_image: '',
  seo_contact_title: 'Contact Saloni Designs | Handcrafted Jewelry & Rakhi',
  seo_contact_description:
    'Contact Saloni Designs for orders, custom jewelry, and enquiries. Call, email, or WhatsApp — we ship across India.',
  seo_contact_og_image: '',
  seo_about_title: 'About Saloni Designs | Handcrafted Jewelry & Rakhi',
  seo_about_description:
    'Learn about Saloni Designs — handcrafted jewelry rooted in tradition, made with care and shipped across India.',
  seo_about_og_image: '',
};

export const SITE_SETTINGS_DEFAULTS = {
  phone: '9680384332',
  email: 'salonidesigns.floral@gmail.com',
  whatsapp: '919680384332',
  location_line: 'India',
  instagram_url: 'https://instagram.com/floraljewelry_salonidesigns',
  instagram_embed_html: '',
  footer_tagline: 'Handcrafted Rakhi & Lumba',
  footer_location: 'India',
  // PWA / Add to Home Screen banner: how long "Not now" hides it (hours).
  pwa_install_dismiss_hours: 1,
  announcement_enabled: false,
  announcement_text: '',
  announcement_link: '',
  hours_mon_fri: '10:00 AM – 7:00 PM',
  hours_sat: '10:00 AM – 6:00 PM',
  hours_sun: 'Closed',
  hours_note:
    'During festival seasons we extend hours when we can. Follow us on Instagram for updates.',
  featured_products: [],
  phone_display: '+91 96803 84332',
  tel_href: 'tel:+919680384332',
  email_href: 'mailto:salonidesigns.floral@gmail.com',
  whatsapp_href: 'https://wa.me/919680384332',
  ...CONTENT_DEFAULTS,
  policies: mergePolicies(null),
  auth_copy: mergeAuthCopy(null),
  store_copy: mergeStoreCopy(null),
  blog_content: mergeBlogContent(null),
  page_copy: mergePageCopy(null),
};

/** Coerce API/MySQL booleans (true, 1, "1") for admin toggles. */
export function coerceSiteSettingBool(val, fallback = false) {
  if (val === true || val === 1 || val === '1' || val === 'true') return true;
  if (val === false || val === 0 || val === '0' || val === 'false') return false;
  return fallback;
}

export const SITE_SETTINGS_FORM_KEYS = [
  'phone',
  'email',
  'whatsapp',
  'location_line',
  'instagram_url',
  'instagram_embed_html',
  'footer_tagline',
  'footer_location',
  'pwa_install_dismiss_hours',
  'announcement_enabled',
  'announcement_text',
  'announcement_link',
  'hours_mon_fri',
  'hours_sat',
  'hours_sun',
  'hours_note',
  'hero_title',
  'hero_subtitle',
  'hero_description',
  'hero_cta1_label',
  'hero_cta1_url',
  'hero_cta2_label',
  'hero_cta2_url',
  'home_about_title',
  'home_about_text',
  'home_about_tagline',
  'about_hero_title',
  'about_hero_subtitle',
  'about_para1',
  'about_para2',
  'about_para3',
  'about_tagline',
  'seo_home_title',
  'seo_home_description',
  'seo_home_og_image',
  'seo_shop_title',
  'seo_shop_description',
  'seo_shop_og_image',
  'seo_contact_title',
  'seo_contact_description',
  'seo_contact_og_image',
  'seo_about_title',
  'seo_about_description',
  'seo_about_og_image',
  'featured_products',
  'policies',
  'auth_copy',
  'store_copy',
  'blog_content',
  'page_copy',
];

export function settingsToAdminForm(s) {
  const src = s && typeof s === 'object' ? s : {};
  const out = {};
  for (const key of SITE_SETTINGS_FORM_KEYS) {
    if (key === 'featured_products') {
      out.featured_products = Array.isArray(src.featured_products)
        ? src.featured_products.map((p) => ({
            category_slug: String(p.category_slug || p.category || '').trim(),
            product_id: String(p.product_id ?? p.id ?? '').trim(),
          }))
        : [];
      continue;
    }
    if (key === 'policies') {
      out.policies = mergePolicies(src.policies);
      continue;
    }
    if (key === 'auth_copy') {
      out.auth_copy = mergeAuthCopy(src.auth_copy);
      continue;
    }
    if (key === 'store_copy') {
      out.store_copy = mergeStoreCopy(src.store_copy);
      continue;
    }
    if (key === 'blog_content') {
      out.blog_content = mergeBlogContent(src.blog_content);
      continue;
    }
    if (key === 'page_copy') {
      out.page_copy = mergePageCopy(src.page_copy);
      continue;
    }
    if (key === 'announcement_enabled') {
      out[key] = coerceSiteSettingBool(src[key], false);
      continue;
    }
    const val = src[key];
    out[key] = val == null ? SITE_SETTINGS_DEFAULTS[key] ?? '' : val;
  }
  return out;
}

export function mergeSiteSettings(apiSettings) {
  if (!apiSettings || typeof apiSettings !== 'object') {
    return { ...SITE_SETTINGS_DEFAULTS };
  }
  const merged = { ...SITE_SETTINGS_DEFAULTS, ...apiSettings };
  merged.featured_products = Array.isArray(apiSettings.featured_products)
    ? apiSettings.featured_products
    : [];
  merged.policies = mergePolicies(apiSettings.policies);
  merged.auth_copy = mergeAuthCopy(apiSettings.auth_copy);
  merged.store_copy = mergeStoreCopy(apiSettings.store_copy);
  merged.blog_content = mergeBlogContent(apiSettings.blog_content);
  merged.page_copy = mergePageCopy(apiSettings.page_copy);
  if (
    process.env.NODE_ENV === 'development' &&
    !String(merged.seo_home_og_image || '').trim() &&
    merged.page_copy?.home?.hero_slides?.some((s) => String(s?.image || '').trim())
  ) {
    const first = merged.page_copy.home.hero_slides.find((s) => String(s?.image || '').trim());
    merged.seo_home_og_image = first?.image || '/demo-hero/photoshoot-1.png';
  }
  merged.announcement_enabled = coerceSiteSettingBool(apiSettings.announcement_enabled, false);
  for (const key of [
    'seo_home_og_image',
    'seo_shop_og_image',
    'seo_contact_og_image',
    'seo_about_og_image',
  ]) {
    const v = apiSettings[key];
    merged[key] = v != null && String(v).trim() !== '' ? String(v).trim() : '';
  }
  return merged;
}
